import { useEffect, useState } from 'react';
import { Lock, Eye, EyeOff, Loader2, User } from 'lucide-react';
import supabase from '../lib/supabase';
import {
  PageHeader, Section, Field, Input, Button, StatusLine,
} from '../components/ui';

/**
 * Per-user account page for coaches and club staff. Shows the signed-in
 * email and lets the user change their own password.
 */
export default function Account() {
  const [email, setEmail] = useState<string | null>(null);
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setEmail(data.user?.email ?? null));
  }, []);

  return (
    <div className="p-8 max-w-2xl">
      <PageHeader
        eyebrow="Partner"
        title="Account"
        subtitle="Your sign-in details for AceRent Partner."
      />

      <Section
        title={<span className="flex items-center gap-2"><User className="w-4 h-4 text-[#2d5016]" /> Signed in as</span>}
      >
        <div className="text-sm text-gray-700">{email ?? 'Loading…'}</div>
      </Section>

      <ChangePasswordSection />
    </div>
  );
}

function ChangePasswordSection() {
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    if (next.length < 8) { setMsg({ kind: 'error', text: 'At least 8 characters.' }); return; }
    if (next !== confirm) { setMsg({ kind: 'error', text: 'Passwords don\u2019t match.' }); return; }
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password: next });
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error.message }); return; }
    setNext(''); setConfirm('');
    setMsg({ kind: 'success', text: 'Password updated. Use the new one next time you sign in.' });
  };

  return (
    <Section
      title={<span className="flex items-center gap-2"><Lock className="w-4 h-4 text-[#2d5016]" /> Change password</span>}
      subtitle="Minimum 8 characters. Takes effect immediately."
    >
      <form onSubmit={submit} className="space-y-3 max-w-sm">
        <Field label="New password">
          <div className="relative">
            <Input type={show ? 'text' : 'password'}
              value={next} onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password" className="pr-9" />
            <button type="button" onClick={() => setShow((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
              aria-label={show ? 'Hide password' : 'Show password'}>
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </Field>
        <Field label="Confirm new password">
          <Input type={show ? 'text' : 'password'}
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password" />
        </Field>
        {msg && <StatusLine kind={msg.kind}>{msg.text}</StatusLine>}
        <div>
          <Button type="submit" loading={busy} icon={Lock}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update password'}
          </Button>
        </div>
      </form>
    </Section>
  );
}
